import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { ApiError, api } from '../api/client';
import { InfoPanel } from '../components/InfoPanel';
import type {
  IssuedReadToken,
  MbtilesEntry,
  ReadTokenSummary,
} from '../api/types';

export function ReadTokensPage() {
  const [tokens, setTokens] = useState<ReadTokenSummary[] | null>(null);
  const [mbtiles, setMbtiles] = useState<MbtilesEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [issued, setIssued] = useState<IssuedReadToken | null>(null);

  const refresh = async () => {
    try {
      const [t, m] = await Promise.all([
        api.listReadTokens(),
        api.listMbtiles(),
      ]);
      setTokens(t);
      setMbtiles(m);
    } catch (err) {
      setError((err as Error).message);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const handleRevoke = async (id: string) => {
    if (!confirm('이 read-token을 회수합니다. 이 토큰을 쓰는 외부 클라이언트는 즉시 401을 받습니다.'))
      return;
    try {
      await api.revokeReadToken(id);
      refresh();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  return (
    <>
      <h2>read-tokens</h2>
      <InfoPanel
        what={
          <p style={{ margin: 0 }}>
            외부 브라우저·앱이 private mbtiles 의 타일을 <strong>읽기</strong>
            전용으로 가져갈 수 있는 자격 증명입니다. 발급 시 어떤 mbtiles 를
            허용/거부할지 (allow/deny) 명시합니다.
          </p>
        }
        why={
          <ul style={{ margin: 0, paddingLeft: '1.2rem' }}>
            <li>
              admin JWT 나 API key 를 외부 사용자에게 직접 주면 권한이 너무 넓고
              위험합니다 — read-token 은 정확히 지정된 mbtiles 의 읽기만 허용해
              피해 범위를 제한합니다.
            </li>
            <li>
              평가 규칙은 <strong>deny 우선 → allow → default-deny</strong>,
              와일드카드는 지원하지 않고 정확 매칭만 합니다. allow 가 비어 있으면
              발급 자체가 막힙니다 (실수로 모든 자원이 잠기는 토큰 방지).
            </li>
            <li>
              plain JWT 는 발급 직후 한 번만 노출됩니다. 외부 클라이언트는 이걸
              <code>Authorization: Bearer …</code> 헤더 또는{' '}
              <code>?token=…</code> 쿼리스트링으로 호출합니다.
            </li>
          </ul>
        }
      />
      {error && (
        <div className="banner error" data-testid="read-tokens-error">
          {error}
        </div>
      )}

      {issued && (
        <div className="banner" data-testid="read-tokens-issued">
          <strong>새 read-token — 이 값은 다시 보여주지 않습니다.</strong>
          <pre
            style={{
              margin: '0.5rem 0',
              padding: '0.5rem',
              background: 'rgba(0,0,0,0.06)',
              wordBreak: 'break-all',
              whiteSpace: 'pre-wrap',
            }}
            data-testid="read-tokens-issued-plain"
          >
            {issued.token}
          </pre>
          <div style={{ fontSize: '0.85rem' }}>
            allow: <code>{issued.permissions.allow.join(', ') || '(없음)'}</code>
            {' · '}
            deny: <code>{issued.permissions.deny.join(', ') || '(없음)'}</code>
          </div>
          <button
            className="btn ghost"
            onClick={() => setIssued(null)}
            data-testid="read-tokens-issued-dismiss"
            style={{ marginTop: '0.5rem' }}
          >
            확인했습니다
          </button>
        </div>
      )}

      <IssueForm
        mbtiles={mbtiles}
        onIssued={(t) => {
          setIssued(t);
          setError(null);
          refresh();
        }}
        onError={setError}
      />

      <div className="card">
        <h3>발급된 토큰</h3>
        {!tokens ? (
          <div>로딩 중…</div>
        ) : tokens.length === 0 ? (
          <div>발급된 read-token이 없습니다.</div>
        ) : (
          <table data-testid="read-tokens-table">
            <thead>
              <tr>
                <th>이름</th>
                <th>allow</th>
                <th>deny</th>
                <th>상태</th>
                <th>발급일</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {tokens.map((t) => (
                <tr key={t.id} data-testid={`read-tokens-row-${t.id}`}>
                  <td>{t.name}</td>
                  <td>
                    <code>{t.permissions.allow.join(', ') || '—'}</code>
                  </td>
                  <td>
                    <code>{t.permissions.deny.join(', ') || '—'}</code>
                  </td>
                  <td>
                    {t.revokedAt ? (
                      <span className="badge private">회수됨</span>
                    ) : (
                      <span className="badge public">활성</span>
                    )}
                  </td>
                  <td>{new Date(t.createdAt).toLocaleString()}</td>
                  <td>
                    {!t.revokedAt && (
                      <button
                        className="btn danger"
                        onClick={() => handleRevoke(t.id)}
                        data-testid={`read-tokens-revoke-${t.id}`}
                      >
                        회수
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

function IssueForm({
  mbtiles,
  onIssued,
  onError,
}: {
  mbtiles: MbtilesEntry[] | null;
  onIssued: (t: IssuedReadToken) => void;
  onError: (msg: string) => void;
}) {
  const [name, setName] = useState('');
  const [allowSet, setAllowSet] = useState<Set<string>>(new Set());
  const [denySet, setDenySet] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);

  const ids = useMemo(() => mbtiles?.map((m) => m.id) ?? [], [mbtiles]);

  const toggle = (
    id: string,
    set: Set<string>,
    setSet: (s: Set<string>) => void,
    other: Set<string>,
    setOther: (s: Set<string>) => void,
  ) => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id);
    else {
      next.add(id);
      // 동일 id가 다른 쪽 집합에 있으면 빼서 일관성 유지 (사용자가 헷갈리지 않게).
      if (other.has(id)) {
        const o = new Set(other);
        o.delete(id);
        setOther(o);
      }
    }
    setSet(next);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const result = await api.issueReadToken({
        name,
        permissions: { allow: [...allowSet], deny: [...denySet] },
      });
      onIssued(result);
      setName('');
      setAllowSet(new Set());
      setDenySet(new Set());
    } catch (err) {
      const msg =
        err instanceof ApiError ? `${err.status}: ${err.message}` : (err as Error).message;
      onError(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card">
      <h3>새 read-token 발급</h3>
      <form onSubmit={handleSubmit} data-testid="read-tokens-form">
        <div className="row" style={{ marginBottom: '0.75rem' }}>
          <input
            type="text"
            value={name}
            placeholder="이름 (예: 농가-앱-2026Q2)"
            required
            onChange={(e) => setName(e.target.value)}
            data-testid="read-tokens-name"
          />
          <button
            className="btn"
            type="submit"
            disabled={!name || busy || allowSet.size === 0}
            data-testid="read-tokens-submit"
          >
            {busy ? '발급 중…' : '발급'}
          </button>
        </div>

        <div style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '0.5rem' }}>
          평가 규칙: deny에 있으면 거부 → allow에 있어야 통과 → 그 외는 default-deny.
          allow가 비면 발급 불가.
        </div>

        {!mbtiles ? (
          <div>mbtiles 목록 로딩 중…</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>id</th>
                <th>allow</th>
                <th>deny</th>
              </tr>
            </thead>
            <tbody>
              {ids.map((id) => (
                <tr key={id}>
                  <td>
                    <code>{id}</code>
                  </td>
                  <td>
                    <input
                      type="checkbox"
                      checked={allowSet.has(id)}
                      onChange={() =>
                        toggle(id, allowSet, setAllowSet, denySet, setDenySet)
                      }
                      data-testid={`read-tokens-allow-${id}`}
                    />
                  </td>
                  <td>
                    <input
                      type="checkbox"
                      checked={denySet.has(id)}
                      onChange={() =>
                        toggle(id, denySet, setDenySet, allowSet, setAllowSet)
                      }
                      data-testid={`read-tokens-deny-${id}`}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </form>
    </div>
  );
}
